import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/utils/articles";
import { getCategoryName } from "@/utils/articles";

export default function HeroCarousel({ items }: { items: Article[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const active = items[index];
  const categoryName = getCategoryName(active.category);

  const go = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  return (
    <section className="relative w-full h-[440px] md:h-[560px] overflow-hidden rounded-lg bg-foreground-950">
      {items.map((item, i) => (
        <img
          key={item.id}
          src={item.image}
          alt={item.title}
          title={`${item.title} - 黑料焦点`}
          className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/55 to-foreground-950/10" />
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/30 to-transparent" />

      <div className="relative z-10 h-full flex flex-col justify-end px-5 md:px-10 pb-8 md:pb-14 text-background-50">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-3 md:mb-4">
            <span className="px-2.5 py-1 rounded bg-primary-500 text-background-50 text-xs font-medium">
              焦点专题
            </span>
            <span className="px-2.5 py-1 rounded bg-background-50/15 text-xs">
              {categoryName}
            </span>
          </div>
          <h1 className="font-heading font-extrabold text-2xl md:text-4xl leading-tight mb-3 md:mb-4">
            {active.title}
          </h1>
          <p className="hidden sm:block text-sm md:text-base text-background-200/90 leading-relaxed mb-5 md:mb-6 line-clamp-2">
            {active.summary}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition cursor-pointer whitespace-nowrap"
            >
              阅读全文
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to={`/category/${active.category}`}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-50/40 text-background-50 text-sm hover:bg-background-50/10 transition cursor-pointer whitespace-nowrap"
            >
              更多{categoryName}
            </Link>
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 md:mt-8">
          <div className="flex items-center gap-2">
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到第${i + 1}条焦点`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  i === index ? "w-7 bg-primary-500" : "w-3 bg-background-50/40 hover:bg-background-50/70"
                }`}
              />
            ))}
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button
              type="button"
              aria-label="上一条"
              onClick={() => go(index - 1)}
              className="w-9 h-9 rounded-full border border-background-50/40 flex items-center justify-center hover:bg-background-50/10 transition cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              type="button"
              aria-label="下一条"
              onClick={() => go(index + 1)}
              className="w-9 h-9 rounded-full border border-background-50/40 flex items-center justify-center hover:bg-background-50/10 transition cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}